import React from 'react';
import { FileUpload } from 'primereact/fileupload';
import { useTranslation } from 'localization';
import CustomDialogAtom from '../../atoms/CustomDialog/CustomDialogAtom';
import MessageAtom from '../../atoms/Message/MessageAtom';
import './UploadAvatarDialogMolecule.scss';

const BYTES_PER_MB = 1024 * 1024;

export interface UploadAvatarDialogMoleculeProps {
  visible: boolean;
  onHide: () => void;
  onUpload: (event: any) => Promise<void>;
  /** Custom dialog title. Defaults to profile.uploadAvatar translation. */
  title?: string;
  /** Custom MIME types for file upload. Defaults to 'image/*'. */
  accept?: string;
  /** Maximum file size in bytes. Defaults to 10000000; `null` sets no limit. */
  maxFileSize?: number | null;
  /** Error message to display. When provided, renders as an inline error. */
  errorMessage?: string;
  /** True while the chosen file is being sent: the picker is disabled and the upload button shows progress. */
  uploading?: boolean;
  /** The upload button's label while `uploading`. Defaults to its usual label. */
  uploadingLabel?: string;
}

const UploadAvatarDialogMolecule: React.FC<UploadAvatarDialogMoleculeProps> = ({
  visible,
  onHide,
  onUpload,
  title,
  accept = 'image/*',
  maxFileSize = 10000000,
  errorMessage,
  uploading = false,
  uploadingLabel,
}) => {
  const { t } = useTranslation();

  return (
    <CustomDialogAtom
      header={title || t('profile.uploadAvatar')}
      visible={visible}
      onHide={onHide}
      className="upload-avatar-dialog"
      style={{ width: '90vw', maxWidth: '520px' }}
    >
      {errorMessage && <MessageAtom severity="error" text={errorMessage} />}
      <FileUpload
        name="avatar"
        accept={accept}
        maxFileSize={maxFileSize ?? undefined}
        // FileUpload rejects an oversize file itself; without these it says so in English, with the file name.
        invalidFileSizeMessageSummary={t('profile.fileTooLargeSummary')}
        invalidFileSizeMessageDetail={
          maxFileSize ? t('profile.fileTooLargeDetail', { size: maxFileSize / BYTES_PER_MB }) : undefined
        }
        customUpload
        uploadHandler={onUpload}
        disabled={uploading}
        chooseLabel={t('profile.selectImage')}
        uploadLabel={uploading ? uploadingLabel ?? t('profile.upload') : t('profile.upload')}
        // A whole object, never `undefined`: FileUpload reads `uploadOptions.iconOnly` unguarded.
        uploadOptions={{ icon: uploading ? 'pi pi-spin pi-spinner' : undefined, iconOnly: false }}
        cancelLabel={t('common.actions.cancel')}
      />
    </CustomDialogAtom>
  );
};

export default UploadAvatarDialogMolecule;
